import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function FeaturesBenefits() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userFeatures, setUserFeatures] = useState([]);
  const [userBenefits, setUserBenefits] = useState([]);
  const [customFeature, setCustomFeature] = useState('');
  const [customBenefit, setCustomBenefit] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load features and benefits
    try {
      const response = await fetch('/api/competitive-analysis/get-features-benefits', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUserFeatures(data.features || []);
        setUserBenefits(data.benefits || []);
      } else {
        setError('Failed to load features and benefits');
      }
    } catch (error) {
      setError('Error loading data: ' + error.message);
    }

    setLoading(false);
  };

  const handleAddCustomFeature = async () => {
    if (!customFeature.trim()) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/competitive-analysis/add-custom-feature', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          feature: customFeature.trim(),
          type: 'feature'
        })
      });

      if (response.ok) {
        setCustomFeature('');
        setSuccess('Feature added successfully!');
        // Reload the list
        const reloadResponse = await fetch('/api/competitive-analysis/get-features-benefits', {
          credentials: 'include'
        });
        if (reloadResponse.ok) {
          const data = await reloadResponse.json();
          setUserFeatures(data.features || []);
          setUserBenefits(data.benefits || []);
        }
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add feature');
      }
    } catch (err) {
      setError('Error adding feature: ' + err.message);
    }

    setSaving(false);
  };

  const handleAddCustomBenefit = async () => {
    if (!customBenefit.trim()) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/competitive-analysis/add-custom-feature', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          feature: customBenefit.trim(),
          type: 'benefit'
        })
      });

      if (response.ok) {
        setCustomBenefit('');
        setSuccess('Benefit added successfully!');
        // Reload the list
        const reloadResponse = await fetch('/api/competitive-analysis/get-features-benefits', {
          credentials: 'include'
        });
        if (reloadResponse.ok) {
          const data = await reloadResponse.json();
          setUserFeatures(data.features || []);
          setUserBenefits(data.benefits || []);
        }
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add benefit');
      }
    } catch (err) {
      setError('Error adding benefit: ' + err.message);
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="business-model" />
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <Card className="shadow">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>📝 Features & Benefits</h2>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard/business-model')}
                >
                  ← Back to Dashboard
                </Button>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              <Alert variant="info" className="mb-4">
                This is your complete list of features and benefits extracted from your sales page and competitive analysis.
              </Alert>

              <Row>
                <Col md={6}>
                  <Card className="mb-4 border-primary">
                    <Card.Header className="bg-primary text-white">
                      <strong>Features ({userFeatures.length})</strong>
                    </Card.Header>
                    <Card.Body>
                      {userFeatures.length === 0 ? (
                        <p className="text-muted small">No features found. Complete the Competitive Intelligence workflow to extract features.</p>
                      ) : (
                        <ul className="mb-0">
                          {userFeatures.map((feature, idx) => (
                            <li key={idx} className="mb-2">{feature}</li>
                          ))}
                        </ul>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="mb-4 border-success">
                    <Card.Header className="bg-success text-white">
                      <strong>Benefits ({userBenefits.length})</strong>
                    </Card.Header>
                    <Card.Body>
                      {userBenefits.length === 0 ? (
                        <p className="text-muted small">No benefits found. Complete the Competitive Intelligence workflow to extract benefits.</p>
                      ) : (
                        <ul className="mb-0">
                          {userBenefits.map((benefit, idx) => (
                            <li key={idx} className="mb-2">{benefit}</li>
                          ))}
                        </ul>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Add Your Own Features/Benefits */}
              <Card className="mb-4 border-primary">
                <Card.Header className="bg-primary text-white">
                  <strong>➕ Add Your Own Features or Benefits</strong>
                </Card.Header>
                <Card.Body>
                  <Alert variant="info" className="mb-3">
                    <small>
                      <strong>Remember something we missed?</strong> Add your own features or benefits here that aren't captured in the lists above.
                    </small>
                  </Alert>

                  <Row>
                    <Col md={6} className="mb-3">
                      <Form.Label>Add a Feature</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          placeholder="E.g., '24/7 customer support'"
                          value={customFeature}
                          onChange={(e) => setCustomFeature(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddCustomFeature();
                            }
                          }}
                          disabled={saving}
                        />
                        <Button
                          variant="primary"
                          onClick={handleAddCustomFeature}
                          disabled={!customFeature.trim() || saving}
                        >
                          {saving ? 'Adding...' : 'Add'}
                        </Button>
                      </div>
                      <Form.Text className="text-muted">
                        Features are what your product HAS or DOES
                      </Form.Text>
                    </Col>

                    <Col md={6} className="mb-3">
                      <Form.Label>Add a Benefit</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="text"
                          placeholder="E.g., 'Save 10 hours per week'"
                          value={customBenefit}
                          onChange={(e) => setCustomBenefit(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddCustomBenefit();
                            }
                          }}
                          disabled={saving}
                        />
                        <Button
                          variant="primary"
                          onClick={handleAddCustomBenefit}
                          disabled={!customBenefit.trim() || saving}
                        >
                          {saving ? 'Adding...' : 'Add'}
                        </Button>
                      </div>
                      <Form.Text className="text-muted">
                        Benefits are the outcomes customers EXPERIENCE
                      </Form.Text>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Alert variant="success" className="mt-3">
                <strong>What's Next:</strong><br />
                We'll use this complete list of features and benefits in <strong>Module 5</strong> to create the sales page and marketing assets of your dreams! Your competitive analysis has given us everything we need to craft compelling, conversion-focused copy that showcases what makes your product unique.
              </Alert>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default FeaturesBenefits;
